import { gql, useQuery } from '@apollo/client';
import { Box, Card } from '@energiebespaarders/symbols';
import { Gray, Small } from '@energiebespaarders/symbols/helpers';
import { padding } from 'polished';
import React from 'react';
import styled from 'styled-components';
import type {
  getAllFaqItemsAndParentSlugsByQuestion,
  getAllFaqItemsAndParentSlugsByQuestionVariables,
  getAllFaqItemsAndParentSlugsByQuestion_allFaqItems,
  getAllFaqItemsAndParentSlugsByQuestion_allFaqTopics,
} from '~/types/generated/cms/getAllFaqItemsAndParentSlugsByQuestion';
import { getDatoClient } from 'src/datoCMS/datoCmsClient';
import HybridLink from '../HybridLink';

const StyledSearchResults = styled(Card)`
  position: absolute;
  width: 100%;
  z-index: ${x => x.theme.z.nav};
  overflow-y: auto;
  max-height: 300px;
`;

const StyledLinkResult = styled(HybridLink)`
  ${padding(2)};
  display: block;
  border-bottom: 1px solid $gray-light;
  white-space: pre-wrap;

  &:hover {
    background: ${x => x.theme.colors.grayLighter};
  }
`;

interface FAQSearchResultsProps {
  searchString: string;
  clearSearch: () => void;
}

export const GET_ALL_FAQ_ITEMS_AND_PARENT_SLUGS_BY_QUESTION = gql`
  query getAllFaqItemsAndParentSlugsByQuestion($question: String!) {
    allFaqItems(filter: { question: { matches: { pattern: $question } } }) {
      __typename
      id
      question
      topic {
        id
        slug
        category {
          slug
          id
          isParent
          parentCategory {
            id
            slug
          }
        }
      }
    }
    allFaqTopics(filter: { title: { matches: { pattern: $question } } }) {
      __typename
      id
      title
      slug
      category {
        slug
        id
        isParent
        parentCategory {
          id
          slug
        }
      }
    }
  }
`;

type SearchResult =
  | getAllFaqItemsAndParentSlugsByQuestion_allFaqItems
  | getAllFaqItemsAndParentSlugsByQuestion_allFaqTopics;

// TODO: re factor so it can support any depth of nested category
export function constructUrl(item: SearchResult) {
  const { slug, category } = item.__typename === 'FaqItemRecord' ? item.topic! : item!;

  const topicSlug = slug;
  const categorySlug = category?.slug;
  const parentCategorySlug = category?.parentCategory?.slug;
  const itemHasParentCategory = !!category?.parentCategory;

  const itemUrl = itemHasParentCategory
    ? `/faq/${parentCategorySlug}/${categorySlug}/${topicSlug}`
    : `/faq/${categorySlug}/${topicSlug}`;

  return item.__typename === 'FaqItemRecord' ? `${itemUrl}#${item.id}` : itemUrl;
}

const FAQSearchResults: React.FC<FAQSearchResultsProps> = ({ searchString, clearSearch }) => {
  const datoClient = getDatoClient(false);

  const { data: matchedItems } = useQuery<
    getAllFaqItemsAndParentSlugsByQuestion,
    getAllFaqItemsAndParentSlugsByQuestionVariables
  >(GET_ALL_FAQ_ITEMS_AND_PARENT_SLUGS_BY_QUESTION, {
    client: datoClient,
    variables: { question: searchString },
  });

  const searchResults = matchedItems
    ? [...matchedItems.allFaqItems, ...matchedItems.allFaqTopics]
    : [];

  return searchString ? (
    <StyledSearchResults>
      {searchResults?.length ? (
        <>
          <div>
            <Small>
              <Gray>Resultaten ({searchResults?.length})</Gray>
            </Small>
          </div>
          {searchResults?.map((item, index) => {
            const isFaqItem = item.__typename === 'FaqItemRecord';

            return (
              <StyledLinkResult key={item.id} to={constructUrl(item)} onClick={clearSearch}>
                <div key={index}>{isFaqItem ? item.question : item.title}</div>
              </StyledLinkResult>
            );
          })}
        </>
      ) : (
        <Box p={3}>Er zijn geen zoekresultaten gevonden.</Box>
      )}
    </StyledSearchResults>
  ) : (
    <></>
  );
};

export default FAQSearchResults;
